import { z } from 'zod';
import { apiGet, apiPost, apiSend } from '@/lib/api';

/**
 * ⚠️ `itemNumber` (`Items.ITEMNUMBER`) to klucz naturalny i jest TEKSTEM — u klientów realnie
 * `'REK-NITRYL-M'`. Kolumny `ITEMID` w SPS nie ma. Nazwą towaru jest `DESCR`.
 */
export const ItemRowSchema = z.object({
  id: z.string(),
  itemNumber: z.string(),
  name: z.string(),
  unitCost: z.string(),
  unit: z.string(),
  /** `Items.PACKETSIZE` — po ile sztuk towar jest pakowany; planogram podpowiada z tego limity. */
  packetSize: z.number().nullable(),
  group: z
    .object({ id: z.string(), name: z.string(), code: z.string(), isSubGroup: z.boolean() })
    .nullable(),
  supplierPartNumber: z.string().nullable(),
  /** `Items.IMAGEFILE` — SAMA NAZWA pliku (indeks dostawcy), katalog rozstrzyga automat po swojej stronie. */
  imageFile: z.string().nullable(),
  /**
   * Czy portal ma BAJTY tego pliku w bibliotece instancji.
   *
   * ⚠️ To nie to samo co `imageFile !== null`. Nazwa mówi, czego SPS szuka na automacie; ta flaga
   * mówi, czy MY umiemy ten obrazek pokazać. Rozjazd między nimi jest normalnym stanem u klienta
   * (biblioteki jeszcze nie zgrano) i ekran ma go rozróżniać, a nie ukrywać pustą ramką.
   */
  hasImage: z.boolean(),
  /**
   * Marka towaru (`Items.BRAND`).
   *
   * ⚠️⚠️ NA LIŚCIE, BO OKNO EDYCJI CZYTA STĄD WARTOŚĆ POCZĄTKOWĄ — i to nie jest wygoda, tylko
   * naprawa kasowania danych klienta. Zanim tu weszła, formularz startował z pustą marką NA
   * SZTYWNO, a zapis szedł jako `P` z kolumną `BRAND`: każde otwarcie „Edycji towaru" i „Zapisz",
   * choćby po to, żeby poprawić cenę, CZYŚCIŁO markę w bazie klienta. Zmierzone na wyspie DBW
   * 25.08.2026 — wpisana marka wróciła z bazy maszyn, a okno pokazało puste pole.
   *
   * `optional`, bo instancja starsza od tej poprawki pola nie odda; ekran zachowa się wtedy
   * jak dotąd, zamiast wywalić walidację całej listy towarów.
   */
  brand: z.string().nullable().optional(),
  /**
   * Ile INNYCH towarów dzieli ten numer u dostawcy. Powyżej zera to realne ryzyko zamówienia
   * złego towaru, więc UI musi to pokazać, a nie tylko przechować.
   */
  supplierPartSharedWith: z.number(),
  obsolete: z.boolean(),
  machines: z.number(),
  compartments: z.number(),
  qtyCurrent: z.number(),
  qtyMax: z.number(),
  /** `null` = brak pojemności w planogramie, czyli procentu NIE MA CZEGO liczyć. */
  fillPercent: z.number().nullable(),
  /**
   * Na ilu automatach stan jest na progu ponownego zamówienia albo pod nim.
   * PRÓG POCHODZI Z SPS (`VendingMachineItems.CRITICALLEVEL`), nie z heurystyki procentowej.
   */
  lowMachines: z.number(),
  /** Na ilu automatach próg w ogóle nie jest ustawiony — tam „na wyczerpaniu" nie ma sensu. */
  machinesWithoutThreshold: z.number(),
  /**
   * ⚠️ DWA OSOBNE POLA. `supplierName` jest `null`, gdy numeru nie ma w słowniku `Suppliers` —
   * a to jest u klienta stan POPRAWNY, bo w SPS nie ma tam klucza obcego. Ekran musi umieć
   * pokazać sam numer, zamiast udawać brak dostawcy.
   */
  supplierNumber: z.string().nullable(),
  supplierName: z.string().nullable(),
  limits: z.number(),
  txCount: z.number(),
  lastTxAt: z.string().nullable(),
  pending: z.boolean(),
});

export type ItemRow = z.infer<typeof ItemRowSchema>;

const ItemsPageSchema = z.object({
  rows: z.array(ItemRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  /** Liczniki dotyczą CAŁEJ wyspy, niezależnie od filtrów listy. */
  summary: z.object({
    total: z.number(),
    assigned: z.number(),
    unassigned: z.number(),
    lowStock: z.number(),
    obsolete: z.number(),
    duplicateSupplierPart: z.number(),
    withoutThreshold: z.number(),
  }),
  byGroup: z.array(z.object({ id: z.string().nullable(), name: z.string(), items: z.number() })),
});

export const ItemGroupOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  /** Klucz złożony podgrupy `'{grupa}_{podgrupa}'`; `null` dla grupy głównej. */
  isgid: z.string().nullable(),
  isSubGroup: z.boolean(),
  parentCode: z.string().nullable(),
  parentName: z.string().nullable(),
  items: z.number(),
});

export type ItemGroupOption = z.infer<typeof ItemGroupOptionSchema>;

/** Stan towaru na konkretnym automacie — trzy sytuacje, które łatwo zlać w jedną. */
export const PRESENCE = ['absent', 'noCapacity', 'stocked'] as const;

const ItemDetailSchema = z.object({
  item: z.object({
    id: z.string(),
    itemNumber: z.string(),
    name: z.string(),
    unitCost: z.string(),
    unit: z.string(),
    supplierPartNumber: z.string().nullable(),
    supplierNumber: z.string().nullable(),
    brand: z.string().nullable(),
    packetSize: z.number().nullable(),
    obsolete: z.boolean(),
    group: z
      .object({
        id: z.string(),
        name: z.string(),
        code: z.string(),
        isgid: z.string().nullable(),
        isSubGroup: z.boolean(),
      })
      .nullable(),
  }),
  machines: z.array(
    z.object({
      machineId: z.string(),
      vmid: z.number(),
      machineName: z.string(),
      maxQty: z.number(),
      minQty: z.number(),
      /** Próg ponownego zamówienia z SPS; 0 = nieustawiony. */
      criticalLevel: z.number(),
      qtyCurrent: z.number(),
      qtyMax: z.number(),
      compartments: z.number(),
      fillPercent: z.number().nullable(),
      presence: z.enum(PRESENCE),
      /** `null` = nie da się powiedzieć: brak progu ALBO brak towaru w bębnie. */
      belowThreshold: z.boolean().nullable(),
    }),
  ),
});

export type ItemDetail = z.infer<typeof ItemDetailSchema>;

export type ListItemsParams = {
  groupId: string;
  search?: string;
  itemGroupId?: string;
  assigned?: boolean;
  fill?: 'empty' | 'low' | 'ok';
  status: 'active' | 'obsolete' | 'all';
  duplicateSupplierPart: boolean;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
};

export const listItems = (p: ListItemsParams) => {
  const q = new URLSearchParams({
    groupId: p.groupId,
    status: p.status,
    duplicateSupplierPart: String(p.duplicateSupplierPart),
    page: String(p.page),
    pageSize: String(p.pageSize),
    sort: p.sort,
    dir: p.dir,
  });
  if (p.search) q.set('search', p.search);
  if (p.itemGroupId) q.set('itemGroupId', p.itemGroupId);
  if (p.assigned !== undefined) q.set('assigned', String(p.assigned));
  if (p.fill) q.set('fill', p.fill);
  return apiGet(`/items?${q.toString()}`, ItemsPageSchema);
};

export const fetchItemGroups = (groupId: string) =>
  apiGet(`/items/groups?groupId=${groupId}`, z.array(ItemGroupOptionSchema));

export const fetchItemDetail = (id: string, groupId: string) =>
  apiGet(`/items/${id}?groupId=${groupId}`, ItemDetailSchema);

/**
 * Czy numer towaru jest wolny.
 *
 * Numeru towaru portal NIE PODPOWIADA — to kod katalogowy nadawany przez człowieka.
 * Sprawdzenie jest niewrażliwe na wielkość liter (kolacja SPS) i patrzy też na kolejkę.
 */
export const checkItemNumber = (groupId: string, itemNumber: string) =>
  apiGet(
    `/items/check-number?groupId=${groupId}&itemNumber=${encodeURIComponent(itemNumber)}`,
    z.object({
      itemNumber: z.string(),
      free: z.boolean(),
      takenBy: z
        .object({ id: z.string(), itemNumber: z.string(), name: z.string(), obsolete: z.boolean() })
        .nullable(),
      pending: z.boolean(),
    }),
  );

const OpSchema = z.object({ opId: z.string() });

export type CreateItemPayload = {
  /** ⚠️ `null` = zdjęcie dostawcy z towaru. Sufit 32 znaki — tyle mieści kolumna w SPS. */
  supplierNumber?: string | null;
  groupId: string;
  itemNumber: string;
  name: string;
  unitCost?: string;
  itemGroupId?: string;
  supplierPartNumber?: string;
  brand?: string;
  packetSize?: number;
};

export const createItem = (payload: CreateItemPayload) =>
  apiSend('POST', '/items', payload, OpSchema.extend({ id: z.string(), itemNumber: z.string() }));

export type UpdateItemPayload = {
  /** ⚠️ `null` = zdjęcie dostawcy z towaru. Sufit 32 znaki — tyle mieści kolumna w SPS. */
  supplierNumber?: string | null;
  name?: string;
  unitCost?: string;
  itemGroupId?: string | null;
  supplierPartNumber?: string | null;
  brand?: string | null;
  packetSize?: number | null;
};

export const updateItem = (id: string, payload: UpdateItemPayload) =>
  apiSend('PATCH', `/items/${id}`, payload, OpSchema);

/**
 * Wycofanie albo przywrócenie towaru (`Items.OBSOLETE`).
 *
 * `ackUnverifiedColumn` jest WYMAGANE i to nie formalność: kolumny `OBSOLETE` nie ma
 * w schemacie odczytanym z produktu SPS — jest opcjonalna per instalacja. Bez potwierdzenia
 * portal obiecywałby wycofanie, które u danego klienta może nie zadziałać.
 */
export const setItemObsolete = (id: string, groupId: string, obsolete: boolean) =>
  apiSend('POST', `/items/${id}/obsolete`, { groupId, obsolete, ackUnverifiedColumn: true }, OpSchema);

export const RenameTargetSchema = z.object({
  table: z.string(),
  column: z.string(),
  rows: z.number(),
  note: z.string(),
  collateral: z.literal(true).optional(),
  beyondSps: z.literal(true).optional(),
});
export type RenameTarget = z.infer<typeof RenameTargetSchema>;

/**
 * Co ruszy zmiana numeru towaru — pytane PRZED kliknięciem.
 *
 * ⚠️ ISTNIEJE PRZEZ DECYZJĘ D1. Kaskada SPS przepisuje `Rationing.ITEMKEY` bez filtru po
 * `ITEMTYPE`, więc zmiana numeru towaru rusza też regułę limitu GRUPY o tym samym kodzie.
 * Zostajemy przy parytecie z SPS, ale wtedy operator musi to zobaczyć zawczasu — stąd `collateral`
 * jest osobną pozycją, a nie liczbą wtopioną w sumę.
 */
export const fetchItemRenameImpact = (id: string, groupId: string) =>
  apiGet(
    `/items/${id}/rename-impact?groupId=${groupId}`,
    z.object({
      item: z.object({ id: z.string(), itemNumber: z.string(), name: z.string() }),
      targets: z.array(RenameTargetSchema),
      totalRows: z.number(),
      collateralRows: z.number(),
    }),
  );

export const zmienNumerTowaru = (id: string, payload: { groupId: string; nowyNumer: string }) =>
  apiSend('POST', `/items/${id}/number`, payload, OpSchema.extend({ stary: z.string(), nowy: z.string() }));

/**
 * Wgranie obrazka towaru.
 *
 * ⚠️ TREŚĆ IDZIE BASE64 W JSON-ie, inaczej niż kopia bazy (bilet prosto do magazynu). Obrazek na
 * ekran automatu to najwyżej kilka megabajtów, więc nie warto otwierać magazynu na zapis
 * z przeglądarki tylko po to.
 */
export const wgrajObrazekTowaru = (
  id: string,
  payload: { groupId: string; nazwaPliku: string; dane: string },
) =>
  apiSend(
    'POST',
    `/items/${id}/obrazek`,
    payload,
    z.object({
      batchId: z.string(),
      automatow: z.number(),
      /** nazwa, POD KTÓRĄ plik faktycznie zapisano — może się różnić od podanej, patrz niżej */
      nazwaPliku: z.string(),
      /**
       * Nazwa sprzed poprawki rozszerzenia albo `null`. API dopasowuje rozszerzenie do TREŚCI
       * pliku, bo okno podpowiada nazwę obecnego obrazka — przy podmianie JPEG-a na PNG-a
       * jednym kliknięciem zapisywało się `.png` pod nazwą `.jpg`.
       */
      poprawionoZ: z.string().nullable(),
      /**
       * Format rozpoznany Z BAJTÓW: `PNG`, `JPEG`, `GIF`, `BMP`.
       *
       * ⚠️ Bez niego komunikat o poprawce mówił tylko „plik jest innego typu, niż mówiła nazwa"
       * — i użytkownik, który wgrał `glut.jpeg`, zobaczył zapis pod `.png` jako kaprys narzędzia.
       * Plik był PNG-iem; brakowało jednego słowa, żeby to było oczywiste.
       */
      format: z.string(),
    }),
  );

/**
 * Zgranie biblioteki obrazków Z WYBRANEGO automatu.
 *
 * ⚠️ WOLNO KLIKNĄĆ DRUGI RAZ. Portal bierze wyłącznie pliki, których jeszcze nie ma, więc
 * ponowienie po zerwanym połączeniu dokłada brakujące zamiast zaczynać całość od nowa.
 *
 * ⚠️⚠️ `agentId` WYSYŁAMY ZAWSZE, odkąd na PESIE zgrywanie trafiło na automat bez ani jednego
 * obrazka („maszyna, z której chciało pobrać, nie miała żadnych obrazków, a pozostałe automaty
 * miały"). Instancja przyjmie też wywołanie bez niego — dla starszego ekranu — ale wtedy wraca
 * do wyboru pierwszego węzła alfabetycznie, czyli do tego, co zawiodło.
 */
export const zgrajObrazkiZAutomatu = (groupId: string, agentId: string) =>
  apiSend(
    'POST',
    '/items/obrazki/zgraj',
    { groupId, agentId },
    z.object({
      zadan: z.number(),
      plikow: z.number(),
      juzMamy: z.number(),
      odrzuconych: z.number(),
      wezel: z.string(),
    }),
  );

const PlikBibliotekiSchema = z.object({
  nazwaPliku: z.string(),
  rozmiar: z.number(),
  /** `portal` = wgrany z ekranu towaru; nazwa węzła = zgrany z tego automatu. */
  zrodlo: z.string(),
  /**
   * Ile towarów wskazuje ten plik. ⚠️ NIE OZDOBNIK: lista jest zbiorem indeksów w rodzaju
   * `P04966881.jpg`, po których nie widać, co jest na zdjęciu — a „używa go 5 towarów" odróżnia
   * plik żywy od sieroty po towarze, którego już nie ma.
   */
  uzywaTowarow: z.number(),
  /** Ile grup artykułów ma ten plik jako kafelek (27.10.2026). `optional`: starsze API go nie oddaje. */
  uzywaGrup: z.number().optional(),
});

export type PlikBiblioteki = z.infer<typeof PlikBibliotekiSchema>;

/**
 * BIBLIOTEKA OBRAZKÓW WYSPY — do wyboru na ekranie towaru.
 *
 * ⚠️ SZUKANIE PO STRONIE SERWERA. Katalog grafik u klienta ma zmierzone ~1500 plików; ściąganie
 * całości do przeglądarki po to, żeby pokazać dziesięć, nie ma uzasadnienia.
 */
/**
 * POKRYCIE OBRAZKÓW — czy każda maszyna ma plik wskazany przez towar.
 *
 * ⚠️ REGUŁA JEST W INSTANCJI (`pokrycie-obrazkow.ts`), tu tylko kształt odpowiedzi. „NIEZMIERZONE"
 * jest TRZECIM stanem obok „ma" i „nie ma" — ekran nie ma prawa zamienić go na brak.
 */
/**
 * ⚠️⚠️ DWA STANY ŻÓŁTE DOŁOŻONE 31.08.2026 na zgłoszenie: „powinno być czerwone pole jak nie ma
 * obrazka wcale, ale żółte jak jest ale jakiś inny niż zakładany".
 *   • `INNY_PLIK`    — ta maszyna wskazuje INNY plik niż master i ten plik u niej leży.
 *   • `INNY_ROZMIAR` — nazwa się zgadza, ale treść jest inna (inny rozmiar niż wzorzec).
 * Czerwone trzeba UZUPEŁNIĆ, żółte trzeba OBEJRZEĆ — to dwie różne reakcje.
 */
export const StanNaMaszynieSchema = z.enum([
  'MA',
  'INNY_PLIK',
  'INNY_ROZMIAR',
  'PUSTY_PLIK',
  'BRAK',
  'NIEZMIERZONE',
]);
export const StanTowaruSchema = z.enum(['BEZ_OBRAZKA', 'WSZEDZIE', 'ROZJAZD', 'BRAKI', 'NIEZMIERZONE']);

/** Jeden wiersz pokrycia — towar albo (od 29.10.2026) kafelek grupy; `towar.itemNumber` grupy to jej klucz SPS. */
const PokrycieWpisSchema = z.object({
      towar: z.object({
        id: z.string(),
        itemNumber: z.string(),
        nazwa: z.string(),
        imageFile: z.string().nullable(),
      }),
      stan: StanTowaruSchema,
      wBibliotece: z.boolean(),
      maszyny: z.array(
        z.object({
          agentId: z.string(),
          nodeId: z.string(),
          automat: z.object({ vmid: z.number(), nazwa: z.string() }).nullable(),
          stan: StanNaMaszynieSchema,
          /** Na co wskazuje TA maszyna, gdy różni się od pliku z mastera — inaczej `null`. */
          plikMaszyny: z.string().nullable(),
          /** Rozmiar pliku, który ta maszyna realnie pokaże; `null` = nie ma go albo nie mierzono. */
          rozmiar: z.number().nullable(),
        }),
      ),
});
export type PokrycieWpis = z.infer<typeof PokrycieWpisSchema>;

export const PokrycieSchema = z.object({
  pokrycie: z.array(PokrycieWpisSchema),
  /** Kafelki grup artykułów — ta sama reguła co towary. Starsze API pola nie oddaje. */
  grupy: z.array(PokrycieWpisSchema).default([]),
  podsumowanie: z.object({
    BEZ_OBRAZKA: z.number(),
    WSZEDZIE: z.number(),
    ROZJAZD: z.number(),
    BRAKI: z.number(),
    NIEZMIERZONE: z.number(),
  }),
  maszyny: z.array(
    z.object({
      agentId: z.string(),
      nodeId: z.string(),
      automat: z.object({ vmid: z.number(), nazwa: z.string() }).nullable(),
      spisAt: z.string().nullable(),
      /** `false` = agent starszy niż spis grafik; ekran mówi „podniesie się sam", nie „awaria". */
      umieSpis: z.boolean(),
      /** Wersja agenta — bez niej „za stary" nie mówi, na co czekać. */
      wersja: z.string().nullable(),
      /** Czy właśnie trwa sprawdzanie na tej maszynie — patrz `wToku` w instancji. */
      wToku: z.boolean(),
      /**
       * OSTATNIA PRÓBA SPRAWDZENIA. `null` = nikt nigdy nie prosił.
       *
       * ⚠️ Bez tego zadanie NIEUDANE wypadało z widoku i wiersz wracał do „nie sprawdzano" —
       * człowiek widział dokładnie to samo co przed kliknięciem, choć portal znał treść błędu.
       */
      ostatniaProba: z
        .object({
          stan: z.string(),
          wToku: z.boolean(),
          blad: z.string().nullable(),
          note: z.string().nullable(),
          kiedy: z.string(),
        })
        .nullable(),
    }),
  ),
  wersjaWymagana: z.string(),
});
export type Pokrycie = z.infer<typeof PokrycieSchema>;

export const fetchPokrycieObrazkow = (groupId: string) =>
  apiGet(`/items/obrazki/pokrycie?groupId=${encodeURIComponent(groupId)}`, PokrycieSchema);

/** ⚠️ Sam odczyt katalogów na maszynach — ani jeden bajt nie jedzie, więc wolno klikać do woli. */
export const zlecSpisObrazkow = (groupId: string) =>
  apiSend(
    'POST',
    '/items/obrazki/spis',
    { groupId },
    z.object({
      zadan: z.number(),
      wezlow: z.number(),
      zaStare: z.array(z.string()),
      wersjaWymagana: z.string(),
    }),
  );

/**
 * ═══════════════════════════════════════════════════════════════════
 *  PLAN SYNCHRONIZACJI KATALOGÓW GRAFIK — co i gdzie trzeba dosłać.
 *
 *  ⚠️⚠️ STANY LICZONE Z PORÓWNANIA LISTINGÓW, nie ze wskaźników: `MA` / `BRAK` / `PUSTY` /
 *  `INNY_ROZMIAR` / `NIEZMIERZONE`. „Nie zmierzono" jest osobnym stanem, bo maszyna bez spisu
 *  nie jest maszyną z pustym katalogiem.
 * ═══════════════════════════════════════════════════════════════════
 */
const PlanSynchronizacjiSchema = z.object({
  pliki: z.array(
    z.object({
      nazwaPliku: z.string(),
      /** Ile towarów wskazuje ten plik — mówi, ile ramek naprawi jedna wysyłka. */
      towarow: z.number(),
      /** `false` = master nie ma bajtów, czyli nie ma czego wysłać. To zamówione ostrzeżenie. */
      wBibliotece: z.boolean(),
      rozmiar: z.number().nullable(),
      stany: z.object({
        MA: z.array(z.string()),
        BRAK: z.array(z.string()),
        PUSTY: z.array(z.string()),
        INNY_ROZMIAR: z.array(z.string()),
        NIEZMIERZONE: z.array(z.string()),
      }),
      /** Maszyny, na które ten plik pojedzie — puste, gdy master go nie ma. */
      doWyslania: z.array(z.string()),
    }),
  ),
  niezmierzoneWezly: z.array(z.object({ agentId: z.string(), nodeId: z.string(), nazwa: z.string() })),
  parDoWyslania: z.number(),
  bezBajtowNaMasterze: z.array(z.string()),
  maszyny: z.array(
    z.object({
      agentId: z.string(),
      nodeId: z.string(),
      nazwa: z.string(),
      spisAt: z.string().nullable(),
    }),
  ),
  maksParNaRaz: z.number(),
});

export type PlanSynchronizacji = z.infer<typeof PlanSynchronizacjiSchema>;

/** ⚠️ Sam odczyt tego, co portal już ma — wolno wołać do woli, nic nie jedzie na maszyny. */
export const fetchPlanSynchronizacji = (groupId: string) =>
  apiGet(`/items/obrazki/plan-synchronizacji?groupId=${encodeURIComponent(groupId)}`, PlanSynchronizacjiSchema);

/**
 * SYNCHRONIZACJA: wysyłka zaznaczonych plików tam, gdzie ich brakuje.
 *
 * ⚠️⚠️ WYSYŁAMY NAZWY PLIKÓW, NIE PARY. Adresatów wylicza instancja z najświeższego spisu —
 * gdyby liczył je ekran, wykonywałby plan sprzed kilku minut.
 *
 * ⚠️ `pomijajNiezmierzone` to JAWNA zgoda człowieka na pominięcie maszyny bez pomiaru. Bez niej
 * instancja odmawia — bo bez spisu nie wiadomo, czego tej maszynie brakuje.
 */
export const synchronizujObrazki = (groupId: string, pliki: readonly string[], pomijajNiezmierzone: boolean) =>
  apiSend(
    'POST',
    '/items/obrazki/synchronizuj',
    { groupId, pliki: [...pliki], pomijajNiezmierzone },
    z.object({
      batchId: z.string().nullable(),
      zadan: z.number(),
      plikow: z.number(),
      pominietePliki: z.array(z.string()),
      automatow: z.number(),
    }),
  );

export const listaObrazkow = (groupId: string, szukaj: string, limit = 50) =>
  apiGet(
    `/items/obrazki?groupId=${encodeURIComponent(groupId)}` +
      `${szukaj === '' ? '' : `&szukaj=${encodeURIComponent(szukaj)}`}&limit=${limit}`,
    z.object({ pliki: z.array(PlikBibliotekiSchema), wiecej: z.boolean() }),
  );

/**
 * Przypisanie towarowi obrazka, który JUŻ jest w bibliotece.
 *
 * ⚠️ MIMO WSZYSTKO ROZSYŁA PLIK NA AUTOMATY — i dlatego oddaje `batchId` tak samo jak wgranie
 * z dysku. Biblioteka powstaje ze zgrania z JEDNEJ maszyny, więc „portal ma plik" nie znaczy
 * „każdy automat go ma"; pominięcie rozesłania dałoby pustą ramkę przy tym automacie, na którym
 * pliku nie było.
 */
/**
 * USUNIĘCIE OBRAZKA Z BIBLIOTEKI — razem z wyczyszczeniem wskaźnika u towarów, które go używały.
 *
 * ⚠️ ZASIĘG ROZSTRZYGNIĘTY PRZEZ UŻYTKOWNIKA (31.08.2026): portal + wskaźnik, a pliki leżące na
 * maszynach zostają. SPS przestaje ich szukać z chwilą wyczyszczenia `Items.IMAGEFILE`.
 */
/**
 * GDZIE JUŻ LEŻY PLIK O TEJ NAZWIE — pod ostrzeżenie przed nadpisaniem.
 *
 * ⚠️ WYSŁANIE OBRAZKA NADPISUJE plik o tej nazwie na KAŻDYM automacie (`writeFile` w katalogu
 * grafik), bez pytania i bez śladu. To zapytanie jest jedynym sposobem, żeby zobaczyć to PRZED
 * kliknięciem — a odpowiedź pochodzi z ostatniego spisu, więc mówi też, czego nie zmierzono.
 */
export const gdzieLezyObrazek = (groupId: string, nazwa: string) =>
  apiGet(
    `/items/obrazki/gdzie-lezy?groupId=${encodeURIComponent(groupId)}&nazwa=${encodeURIComponent(nazwa)}`,
    z.object({
      nazwaPliku: z.string(),
      maszyny: z.array(
        z.object({
          agentId: z.string(),
          nodeId: z.string(),
          automat: z.object({ vmid: z.number(), nazwa: z.string() }).nullable(),
          rozmiar: z.number(),
        }),
      ),
      niezmierzone: z.array(
        z.object({
          agentId: z.string(),
          nodeId: z.string(),
          automat: z.object({ vmid: z.number(), nazwa: z.string() }).nullable(),
        }),
      ),
      wBibliotece: z
        .object({
          rozmiar: z.number(),
          /** `portal` = wgrany z ekranu; nazwa węzła = zgrany z tego automatu. */
          zrodlo: z.string(),
          zmieniono: z.string(),
        })
        .nullable(),
      /** Towary wskazujące ten plik — lista UCIĘTA, licznik pełny (`towarow`). */
      towary: z.array(z.object({ id: z.string(), itemNumber: z.string(), nazwa: z.string() })),
      towarow: z.number(),
      zmierzonoOd: z.string().nullable(),
    }),
  );

/**
 * WGRANIE PLIKU DO SAMEJ BIBLIOTEKI — bez towaru i bez rozsyłania na automaty.
 *
 * ⚠️ INNA OPERACJA NIŻ `wgrajObrazekTowaru`, choć oba wysyłają bajty: tamta przypisuje plik TOWAROWI
 * i wysyła go na wszystkie automaty, ta tylko odkłada go u nas. Zgłoszone (31.08.2026): „w galerii
 * opcja dodania pliku z komputera" — przygotowanie biblioteki jest osobną robotą i nie ma powodu,
 * żeby wymagało wybrania towaru.
 */
export const wgrajObrazekDoBiblioteki = (groupId: string, nazwaPliku: string, dane: string) =>
  apiPost(
    '/items/obrazki/wgraj',
    { groupId, nazwaPliku, dane },
    z.object({
      nazwaPliku: z.string(),
      poprawionoZ: z.string().nullable(),
      format: z.string(),
      rozmiar: z.number(),
      /** `true` = plik o tej nazwie już był i został podmieniony. */
      nadpisano: z.boolean(),
    }),
  );

/**
 * ZMIANA NAZWY PLIKU W BIBLIOTECE.
 *
 * ⚠️ `takzePrzypisane` DOMYŚLNIE `true` (wymóg ze zgłoszenia): bez przepisania wskaźników towary
 * dalej wskazywałyby STARĄ nazwę, której portal już nie ma — czyli zmiana nazwy sama produkowałaby
 * stan „portal nie ma tego pliku". Wtedy plik jedzie też na automaty pod nową nazwą, a wskaźniki
 * zapisują się dopiero, gdy potwierdzą WSZYSTKIE.
 */
export const zmienNazwePliku = (
  groupId: string,
  nazwaPliku: string,
  nowaNazwa: string,
  takzePrzypisane: boolean,
) =>
  apiPost(
    `/items/obrazki/${encodeURIComponent(nazwaPliku)}/zmien-nazwe`,
    { groupId, nowaNazwa, takzePrzypisane },
    z.object({
      nazwaPliku: z.string(),
      nowaNazwa: z.string(),
      /** Ilu towarom przepiszemy wskaźnik po dosłaniu pliku na automaty. */
      przepisano: z.number(),
      /** Ilu grupom artykułów (kafelki SPSUI) — od 27.10.2026. */
      przepisanoGrup: z.number().optional(),
      automatow: z.number(),
      batchId: z.string().nullable(),
    }),
  );

/**
 * PODGLĄD PLIKU OBRAZKA Z KONKRETNEJ MASZYNY — zlecenie i odczyt stanu.
 *
 * ⚠️ TEN SAM KANAŁ CO ZRZUT EKRANU: agent czyta plik w swoim cyklu, bajty idą własną trasą i mają
 * termin ważności (kwadrans). Zgłoszone (31.08.2026): „gdy plik się nie zgadza, to możliwość
 * wyświetlenia go".
 */
export const zlecPodgladObrazka = (groupId: string, agentId: string, nazwaPliku: string) =>
  apiPost(
    '/items/obrazki/podglad-z-maszyny',
    { groupId, agentId, nazwaPliku },
    z.object({ zadanieId: z.string(), nodeId: z.string(), nazwaPliku: z.string() }),
  );

export const pobierzPodgladObrazka = (groupId: string, zadanieId: string) =>
  apiGet(
    `/items/obrazki/podglad-z-maszyny/${encodeURIComponent(zadanieId)}?groupId=${encodeURIComponent(groupId)}`,
    z.object({
      /** `GOTOWY` = mamy bajty; `WYGASL` = obraz przepadł po kwadransie; inaczej stan zadania. */
      status: z.string(),
      nodeId: z.string(),
      nazwaPliku: z.string().nullable(),
      /** `data:`-URL albo `null`, dopóki nie ma bajtów. */
      obraz: z.string().nullable(),
      bajtow: z.number().nullable(),
      error: z.string().nullable(),
    }),
  );

export const usunObrazekZBiblioteki = (groupId: string, nazwaPliku: string) =>
  apiSend(
    'DELETE',
    `/items/obrazki/${encodeURIComponent(nazwaPliku)}?groupId=${encodeURIComponent(groupId)}`,
    undefined,
    z.object({
      nazwaPliku: z.string(),
      wyczyszczonoTowarow: z.number(),
      wyczyszczonoGrup: z.number().optional(),
      bajtyUsuniete: z.boolean(),
    }),
  );

export const przypiszObrazekZBiblioteki = (id: string, payload: { groupId: string; nazwaPliku: string }) =>
  apiSend(
    'POST',
    `/items/${id}/obrazek-z-biblioteki`,
    payload,
    z.object({ batchId: z.string(), automatow: z.number(), nazwaPliku: z.string() }),
  );

/**
 * OBRAZEK GRUPY ARTYKUŁÓW (27.10.2026) — kafelek grupy albo podgrupy w SPSUI. Ta sama odpowiedź
 * co przy zdjęciu towaru, bo to ta sama wysyłka na wszystkie automaty; `id` to id węzła drzewa grup.
 */
export const wgrajObrazekGrupy = (grupaId: string, payload: { groupId: string; nazwaPliku: string; dane: string }) =>
  apiSend(
    'POST',
    `/items/grupy/${grupaId}/obrazek`,
    payload,
    z.object({
      batchId: z.string(),
      automatow: z.number(),
      nazwaPliku: z.string(),
      poprawionoZ: z.string().nullable(),
      format: z.string(),
    }),
  );

export const przypiszObrazekGrupie = (grupaId: string, payload: { groupId: string; nazwaPliku: string }) =>
  apiSend(
    'POST',
    `/items/grupy/${grupaId}/obrazek-z-biblioteki`,
    payload,
    z.object({ batchId: z.string(), automatow: z.number(), nazwaPliku: z.string() }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  WYSYŁKI ZDJĘĆ W TOKU — znacznik przy artykule po zamknięciu okna (24.09.2026).
 *
 *  ⚠️ NAJNOWSZA WYSYŁKA KAŻDEGO ARTYKUŁU, liczona na serwerze (`wysylki-obrazkow.ts`). Starsze są
 *  unieważniane przy nowej, więc ekran nie ma tu żadnej reguły do powtórzenia.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
const WysylkaObrazkaSchema = z.object({
  batchId: z.string(),
  nazwaPliku: z.string(),
  towary: z.array(z.string()),
  /** Grupy artykułów, których kafelek jedzie tą wysyłką (27.10.2026). Starsze API pola nie ma. */
  grupy: z.array(z.object({ tabela: z.enum(['ItemGroups', 'ItemSubGroups']), klucz: z.string() })).default([]),
  razem: z.number(),
  gotowe: z.number(),
  czekaNa: z.array(z.string()),
  bledy: z.array(z.object({ automat: z.string(), powod: z.string() })),
  od: z.string(),
});

export type WysylkaObrazka = z.infer<typeof WysylkaObrazkaSchema>;

export const fetchWysylkiObrazkow = (groupId: string) =>
  apiGet(`/items/obrazki/wysylki?groupId=${encodeURIComponent(groupId)}`, z.object({ wysylki: z.array(WysylkaObrazkaSchema) }));

/** „Ponów" — nieudane automaty wracają do kolejki; te, które plik mają, zostają nietknięte. */
export const ponowWysylkeObrazka = (groupId: string, batchId: string) =>
  apiSend('POST', `/items/obrazki/wysylki/${batchId}/ponow`, { groupId }, z.object({ ponowiono: z.number() }));
